import { useBoxPlanningSlots } from "@/modules/boxPlanning/hooks/useBoxPlanningSlots"
import { useBoxSlotsBeingCreated, useBoxSlotsBeingViewed } from "@/modules/boxPlanning/store/useBoxPlanningStore"
import { cn } from "@/modules/lib/utils"
import { BoxPlanningCalendarEvent } from "@modules/boxPlanning/components/boxPlanningCalendarEvent"
import { BoxSlotTemplateToolComponent } from "@modules/boxProgrammation/components/boxSlotTemplateToolComponent"
import { useBoxSlotSidebar } from "@modules/boxSlot/hooks/useBoxSlotSidebar"
import { BoxStaffPermissions } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@modules/boxStaff/hooks/useBoxStaffPermissions"
import { Calendar } from "@modules/calendar/calendar"
import { type CalendarEventTypeWithHour } from "@modules/calendar/calendarTypes"
import { useCalendar } from "@modules/calendar/hooks/useCalendar"
import { useServicesContext } from "@modules/core/services/services.context"
import { LocalStorageKeys } from "@modules/storage/localStorageKeys"
import dayjs from "dayjs"
import { useCallback, useEffect, useEffectEvent, useMemo, useState } from "react"
import { useParams } from "react-router"

export function BoxPlanningCalendar({
  className,
  ...props
}: {
  className?: string
} & React.ComponentProps<"div">) {
  const { boxProgramId } = useParams()
  const { getCalendarRange } = useCalendar()
  const { startDate, endDate } = getCalendarRange()

  const { data: allSlots } = useBoxPlanningSlots(
    dayjs.isDayjs(dayjs(startDate)) ? dayjs(startDate).toDate() : undefined,
    dayjs.isDayjs(dayjs(endDate)) ? dayjs(endDate).toDate() : undefined,
  )

  const { localStorageService } = useServicesContext()
  const { navigateToBoxSlotView, navigateToBoxSlotCreation } = useBoxSlotSidebar()
  const boxSlotsBeingViewed = useBoxSlotsBeingViewed()
  const boxSlotsBeingCreated = useBoxSlotsBeingCreated()
  const filteredSlots = useMemo(() => {
    const createdIds = new Set(boxSlotsBeingCreated.map((slot) => slot.id))
    const filteredBaseSlots = allSlots.filter((slot) => !createdIds.has(slot.id))
    return [
      ...filteredBaseSlots.map((slot) => slot.toCalendarEventTypeWithHour()),
      ...boxSlotsBeingCreated.map((slotDraft) => slotDraft.toCalendarEventTypeWithHour()),
    ]
  }, [allSlots, boxSlotsBeingCreated])

  const canUserCreateSlot = useBoxStaffPermission(BoxStaffPermissions.MANAGE_SLOTS)
  const [showTemplateExplanation, setShowTemplateExplanation] = useState<boolean>(allSlots.length === 0 ? true : false)

  const isEventFromCurrentProgrammation = useCallback(
    (event: CalendarEventTypeWithHour) => {
      return event.programmationId && boxProgramId ? event.programmationId === boxProgramId : true
    },
    [boxProgramId],
  )

  const isEventOverlayed = useCallback(
    (event: CalendarEventTypeWithHour) => {
      return Array.isArray(boxSlotsBeingCreated) && boxSlotsBeingCreated.length > 0
        ? boxSlotsBeingCreated.findIndex((slotBegingCreated) => slotBegingCreated.id === event.id) >= 0
        : boxSlotsBeingViewed
          ? boxSlotsBeingViewed === event.id
          : isEventFromCurrentProgrammation(event)
    },
    [isEventFromCurrentProgrammation, boxSlotsBeingCreated, boxSlotsBeingViewed],
  )

  const isEventByClickable = useCallback(
    (event: CalendarEventTypeWithHour) => {
      return !boxProgramId || event.programmationId === boxProgramId
    },
    [boxProgramId],
  )

  const onAddEventClick = (value?: Date | number) => {
    if (value && typeof value === "object" && boxProgramId) {
      navigateToBoxSlotCreation(boxProgramId, value)
    }
  }

  const relatedSlot = useCallback(
    (eventId: string) => {
      return allSlots.find((slot) => slot.id === eventId)
    },
    [allSlots],
  )

  const hideTemplateExplanation = () => {
    setShowTemplateExplanation(false)
    localStorageService.save(LocalStorageKeys.boxSlotTemplate.dontShowOnBoardingAnymore, { value: true })
  }

  const showTemplateExplanationEvent = useEffectEvent(() => {
    if (filteredSlots.length > 0) {
      const dontShowOnBoardingAnymore = localStorageService.load(
        LocalStorageKeys.boxSlotTemplate.dontShowOnBoardingAnymore,
        { value: filteredSlots.length > 0 },
      )
      setShowTemplateExplanation(!dontShowOnBoardingAnymore.value)
    }
  })

  useEffect(() => {
    showTemplateExplanationEvent()
  }, [])

  return (
    <div className={cn("pointer-events-auto flex grow flex-col items-center", className)} {...props}>
      {showTemplateExplanation ? (
        <BoxSlotTemplateToolComponent hideTemplateExplanation={hideTemplateExplanation} />
      ) : (
        <Calendar
          calendarEvents={[...filteredSlots]}
          canAddEvents={canUserCreateSlot && !!boxProgramId}
          onAddEventClick={onAddEventClick}
          customRenderEvent={(_, event) => (
            <BoxPlanningCalendarEvent
              event={event as CalendarEventTypeWithHour}
              key={event.id}
              slot={relatedSlot(event.id)}
              onClickSlot={() => {
                if (
                  "programmationId" in event &&
                  relatedSlot(event.id) &&
                  (!boxProgramId || event.programmationId === boxProgramId)
                ) {
                  void navigateToBoxSlotView(relatedSlot(event.id)?.id ?? "")
                }
              }}
              canBeClicked={isEventByClickable(event as CalendarEventTypeWithHour)}
              isOverlayed={isEventOverlayed(event as CalendarEventTypeWithHour)}
            />
          )}
        />
      )}
    </div>
  )
}
